import { Routes, CanDeactivateFn } from '@angular/router';
import { Observable } from 'rxjs';
import { authRoutes } from './core/auth/auth.routes';
import { isAuthenticated } from './core/auth/guards/auth.guard';
import { hasPermissions } from './core/auth/guards/permissions.guard';
import { hasDamageRole } from './core/auth/guards/roles.guard';
import { AdminPermissions, DamagePermissions, NewDamagePermissions, NewScreenPermissions } from './core/auth/models/permissions';
import { lowerEnvironmentOnly } from './core/environment/lower-environment.guard';

const unsavedChangesGuard: CanDeactivateFn<{ canDeactivate(): Observable<boolean> | boolean }> =
  (component) => component.canDeactivate();
import { MainLayoutComponent } from './core/layouts/main-layout/main-layout.component';
import { PageNotFoundComponent } from './core/pages/page-not-found/page-not-found.component';
import { SinglePageLayoutComponent } from './core/layouts/single-page-layout/single-page-layout.component';

export const routes: Routes = [
  ...authRoutes,
  { path: '', redirectTo: 'tasks', pathMatch: 'full' },

  {
    path: '',
    component: MainLayoutComponent,
    canActivate: [isAuthenticated, hasDamageRole],
    runGuardsAndResolvers: 'always',
    children: [
      {
        path: 'all-incidents',
        loadComponent: () =>
          import(
            './features/incidents/pages/all-incidents/all-incidents.component'
          ).then((m) => m.AllIncidentsComponent),
        canActivate: [hasPermissions],
        data: { permissions: [NewScreenPermissions.ALL_INCIDENTS] },
        children: [
          {
            path: 'incidents/:damage_code',
            canDeactivate: [unsavedChangesGuard],
            runGuardsAndResolvers: 'always',
            loadComponent: () =>
              import('./features/incidents/pages/incident-detail/incident-detail.component')
            .then((m) => m.IncidentDetailComponent),
          },
        ],
      },
      {
        path: 'in-progress',
        loadComponent: () =>
          import(
            './features/incidents/pages/in-progress/in-progress.component'
          ).then((m) => m.InProgressComponent),
      },
      {
        path: 'damage-review',
        canDeactivate: [unsavedChangesGuard],
        runGuardsAndResolvers: 'always',
        loadComponent: () =>
          import(
            './features/incidents/pages/damage-review/damage-review.component'
          ).then((m) => m.DamageReviewComponent),
        canActivate: [hasPermissions],
        data: { permissions: [DamagePermissions.DAMAGE_REVIEW_SCREEN, NewDamagePermissions.DAMAGE_REVIEW_SCREEN] },
      },
      {
        path: 'damage-review/:damage_input_code',
        canDeactivate: [unsavedChangesGuard],
        runGuardsAndResolvers: 'always',
        loadComponent: () =>
          import(
            './features/incidents/pages/damage-review/damage-review.component'
          ).then((m) => m.DamageReviewComponent),
        canActivate: [hasPermissions],
        data: { permissions: [DamagePermissions.DAMAGE_REVIEW_SCREEN, NewDamagePermissions.DAMAGE_REVIEW_SCREEN] },
      },
      {
        path: 'invoiced',
        loadComponent: () =>
          import('./features/incidents/pages/invoiced/invoiced.component').then(
            (m) => m.InvoicedComponent
          ),
      },
      {
        path: 'incidents/:damage_code',
        canDeactivate: [unsavedChangesGuard],
        runGuardsAndResolvers: 'always',
        loadComponent: () =>
          import(
            './features/incidents/pages/incident-detail/incident-detail.component'
          ).then((m) => m.IncidentDetailComponent),
      },
      {
        // Task and incident full pages render over the list, which stays mounted
        // so closing them does not reload it.
        path: 'tasks',
        loadComponent: () =>
          import(
            './features/incidents/pages/tasks-for-me/tasks-for-me.component'
          ).then((m) => m.TasksForMeComponent),
        children: [
          {
            path: 'incidents/:damage_code',
            canDeactivate: [unsavedChangesGuard],
            runGuardsAndResolvers: 'always',
            loadComponent: () =>
              import(
                './features/incidents/pages/incident-detail/incident-detail.component'
              ).then((m) => m.IncidentDetailComponent),
          },
          {
            path: ':task_code',
            loadComponent: () =>
              import(
                './features/incidents/pages/task-detail/task-detail.component'
              ).then((m) => m.TaskDetailComponent),
            canDeactivate: [unsavedChangesGuard],
            runGuardsAndResolvers: 'always',
          },
        ],
      },
      {
        path: 'damage-form',
        canDeactivate: [unsavedChangesGuard],
        runGuardsAndResolvers: 'always',
        loadComponent: () =>
          import(
            './features/incidents/components/damage-form/damage-form.component'
          ).then((m) => m.DamageFormComponent),
        canActivate: [hasPermissions],
        data: { permissions: [DamagePermissions.CREATE_DAMAGE_INPUT, NewDamagePermissions.CREATE_DAMAGE_INPUT] },
      },
      {
        path: 'permission-overrides',
        loadComponent: () =>
          import('./features/settings/pages/permission-overrides/permission-overrides.component').then(
            (m) => m.PermissionOverridesComponent
          ),
        canActivate: [lowerEnvironmentOnly],
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('./features/settings/pages/settings/settings.component').then(
            (m) => m.SettingsComponent
          ),
        canDeactivate: [unsavedChangesGuard],
      },
      {
        path: 'system-config',
        loadComponent: () =>
          import('./features/settings/pages/system-config/system-config.component').then(
            (m) => m.SystemConfigComponent
          ),
        canActivate: [hasPermissions],
        data: { permissions: [AdminPermissions.ACCESS_CONFIG_PAGES] },
      },
    ],
  },
  {
    path: '',
    component: SinglePageLayoutComponent,
    canActivate: [isAuthenticated, hasDamageRole],
    runGuardsAndResolvers: 'always',
    children: [
      // {
      //   path: 'ai-review',
      //   loadComponent: () =>
      //     import(
      //       './features/incidents/components/internal-ai-review/internal-ai-review.component'
      //     ).then((m) => m.InternalAiReviewComponent),
      // },
      // {
      //   path: 'ai-details/:image_pair_id',
      //   loadComponent: () =>
      //     import(
      //       './features/incidents/components/internal-ai-details/internal-ai-details.component'
      //     ).then((m) => m.InternalAiDetailsComponent),
      // },
    ]
  },
  {
    path: '**',
    component: PageNotFoundComponent,
  },
];
